// src/components/HowItWorks.jsx
import Reveal from "./Reveal";

/* Simple outline icons (no external libs) */
function IconCheck(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}
function IconChat(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
    </svg>
  );
}
function IconPen(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}
function IconShield(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

export default function HowItWorks() {
  const steps = [
    {
      n: "01",
      title: "Check Eligibility",
      text: "Answer a few quick questions to see if your case qualifies.",
      Icon: IconCheck,
    },
    {
      n: "02",
      title: "Free Case Review",
      text: "We connect you with an attorney for a no-obligation review.",
      Icon: IconChat,
    },
    {
      n: "03",
      title: "Sign & File",
      text: "E-sign documents securely; your attorney files your claim.",
      Icon: IconPen,
    },
    {
      n: "04",
      title: "We Fight For You",
      text: "Your legal team works toward the best compensation available.",
      Icon: IconShield,
    },
  ];

  return (
    <section
      id="how-it-works"
      className="relative overflow-hidden py-16 md:py-20"
      style={{
        background:
          "linear-gradient(180deg, #F8F4E9 0%, #F3EAD8 55%, #EFE1C7 100%)",
      }}
    >
      {/* Soft warmth at the bottom for depth (no hairline) */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 380px at 50% 100%, rgba(200,155,60,0.12) 0%, rgba(200,155,60,0) 70%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-semibold bg-[#F3E7C9] text-[#916F20] mb-4">
            How It Works
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A2540]">
            Simple Steps To Start Your Claim
          </h2>
          <p className="text-slate-700 mt-2 max-w-2xl mx-auto">
            Fast, confidential, and free to start—no fees unless you win.
          </p>
        </div>

        {/* Steps + dotted connector */}
        <div className="relative mt-10 md:mt-12">
          {/* Horizontal dotted connector (desktop/tablet) */}
          <div
            className="pointer-events-none absolute left-0 right-0 top-[54px] hidden sm:block"
            style={{
              height: "2px",
              background:
                "repeating-linear-gradient(to right, rgba(200,155,60,0.45) 0, rgba(200,155,60,0.45) 10px, transparent 10px, transparent 22px)",
            }}
            aria-hidden="true"
          />
          {/* Vertical dotted connector (mobile) */}
          <div
            className="pointer-events-none absolute left-[30px] top-0 bottom-0 sm:hidden"
            style={{
              width: "2px",
              background:
                "repeating-linear-gradient(to bottom, rgba(200,155,60,0.45) 0, rgba(200,155,60,0.45) 10px, transparent 10px, transparent 22px)",
            }}
            aria-hidden="true"
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch relative z-10">
            {steps.map((s, i) => {
              const Icon = s.Icon;
              return (
                <Reveal key={s.n} delay={i * 90} className="h-full">
                  <div
                    className="h-full group bg-white rounded-2xl border border-slate-200/80 p-6 text-left
                               shadow-[0_10px_30px_rgba(0,0,0,0.08)]
                               transition-all duration-300
                               hover:-translate-y-2 hover:border-[#C89B3C]
                               hover:ring-2 hover:ring-[#C89B3C]/25"
                  >
                    {/* Top row: number + icon */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#C89B3C]/12 text-[#0A2540] grid place-items-center font-bold">
                        {s.n}
                      </div>
                      {/* CHANGED: icon now in exact brand gold */}
                      <Icon className="w-6 h-6 text-[#C89B3C]" />
                    </div>

                    {/* CHANGED: headline with exact-gold underline */}
                    <h3 className="font-semibold text-lg text-[#0A2540]">
                      <span className="underline decoration-[#C89B3C] decoration-2 underline-offset-4">
                        {s.title}
                      </span>
                    </h3>

                    <p className="text-slate-600 text-sm mt-2">{s.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
